// src/config/oauth.ts
// OAuth provider configuration - extensible for multiple providers

/**
 * Supported OAuth provider identifiers
 * Add new providers here as they are implemented
 */
export const OAuthProviders = {
  GOOGLE: 'google',
  MICROSOFT: 'microsoft',
  // Future providers:
  // GITHUB: 'github',
  // APPLE: 'apple',
} as const;

export type OAuthProviderType = typeof OAuthProviders[keyof typeof OAuthProviders];

/**
 * OAuth provider configuration interface
 */
export interface OAuthProviderConfig {
  /** Unique provider identifier */
  id: OAuthProviderType;
  /** Display name for UI */
  displayName: string;
  /** Whether this provider is currently enabled */
  enabled: boolean;
  /** Client ID from the provider's developer console */
  clientId: string;
  /** OAuth scopes to request */
  scopes: string[];
  /** Provider-specific additional configuration */
  options?: Record<string, unknown>;
}

/**
 * OAuth configuration constants
 */
export const OAUTH_CONFIG = {
  /** Timeout for OAuth popup/redirect operations (ms) */
  TIMEOUT_MS: 120000,
  /** Key used for storing OAuth state in sessionStorage */
  STATE_STORAGE_KEY: 'oauth_state',
  /** Error messages */
  ERRORS: {
    PROVIDER_NOT_CONFIGURED: 'OAuth provider is not configured',
    PROVIDER_DISABLED: 'OAuth provider is currently disabled',
    MISSING_CLIENT_ID: 'OAuth client ID is not configured',
    AUTHENTICATION_FAILED: 'OAuth authentication failed',
    POPUP_CLOSED: 'Authentication popup was closed',
    NETWORK_ERROR: 'Network error during authentication',
    INVALID_RESPONSE: 'Invalid response from OAuth provider',
  },
  /** Analytics event names */
  ANALYTICS_EVENTS: {
    LOGIN_STARTED: 'OAuth Login Started',
    LOGIN_SUCCESS: 'OAuth Login Success',
    LOGIN_FAILED: 'OAuth Login Failed',
    SIGNUP_SUCCESS: 'OAuth Signup Success',
  },
} as const;

/**
 * Google OAuth specific configuration
 */
export const GOOGLE_OAUTH_CONFIG: OAuthProviderConfig = {
  id: OAuthProviders.GOOGLE,
  displayName: 'Google',
  enabled: true,
  clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
  scopes: ['email', 'profile'],
  options: {
    ux_mode: 'popup', // 'popup' or 'redirect'
    context: 'signin', // 'signin', 'signup', or 'use'
  },
};

/**
 * Microsoft OAuth specific configuration
 */
export const MICROSOFT_OAUTH_CONFIG: OAuthProviderConfig = {
  id: OAuthProviders.MICROSOFT,
  displayName: 'Microsoft',
  enabled: true,
  clientId: import.meta.env.VITE_MICROSOFT_CLIENT_ID || '',
  scopes: ['User.Read', 'email', 'profile', 'openid'],
  options: {
    authority: 'https://login.microsoftonline.com/common',
  },
};

/**
 * Map of all OAuth provider configurations
 */
export const oauthProviders: Record<OAuthProviderType, OAuthProviderConfig> = {
  [OAuthProviders.GOOGLE]: GOOGLE_OAUTH_CONFIG,
  [OAuthProviders.MICROSOFT]: MICROSOFT_OAUTH_CONFIG,
};

/**
 * Get configuration for a specific OAuth provider
 */
export function getOAuthProviderConfig(provider: OAuthProviderType): OAuthProviderConfig | null {
  return oauthProviders[provider] || null;
}

/**
 * Get all enabled OAuth providers
 */
export function getEnabledOAuthProviders(): OAuthProviderConfig[] {
  return Object.values(oauthProviders).filter(
    (config) => config.enabled && config.clientId
  );
}

/**
 * Check if a provider is properly configured and enabled
 */
export function isOAuthProviderAvailable(provider: OAuthProviderType): boolean {
  const config = getOAuthProviderConfig(provider);
  return config !== null && config.enabled && Boolean(config.clientId);
}
